import {
  createLocalTracks,
  LocalAudioTrack,
  LocalDataTrack,
  LocalTrack,
  LocalTrackPublication,
  LocalVideoTrack,
  RemoteTrack,
} from 'twilio-video'

import EventBus, { VideoCallEvents } from '@shared/event-bus'
import { ParticipantRole } from '@shared/models/interfaces/conversations'
import {
  RtcSession,
  RtcSessionStatus,
} from '@shared/models/proto/base/rtc-session'
import { Nullable } from '@shared/models/types/primary'
import {
  getMediaErrorType,
  MediaErrorType,
} from '@shared/utils/helpers/error-guards/media-errors'

import { UserType } from '@protots/user_type'

export const getUserMediaTrack = async () => {
  let tracks: MediaStream | undefined
  let mediaError: Nullable<MediaErrorType> = null

  try {
    tracks = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    })
  } catch (e) {
    mediaError = getMediaErrorType(e)

    if (!mediaError) {
      throw e
    }
  }

  return { tracks, mediaError }
}

export const initTracksAndDevices = async (
  tracks: MediaStream,
  initAudio: boolean,
  initVideo: boolean
) => {
  tracks.getTracks().forEach((track) => track.stop())

  const devices = await navigator.mediaDevices.enumerateDevices()
  const cameraDevices = devices.filter(({ kind }) => kind === 'videoinput')
  const microphoneDevices = devices.filter(({ kind }) => kind === 'audioinput')

  const localTrackOptions = { audio: initAudio, video: initVideo }
  const localTracks = await createLocalTracks(localTrackOptions)
  const localDataTrack = new LocalDataTrack()

  EventBus.emit(VideoCallEvents.AttachTracks, {
    tracks: localTracks as (LocalAudioTrack | LocalVideoTrack)[],
    isLocal: true,
  })

  return {
    cameraDevices,
    microphoneDevices,
    tracks: [...localTracks, localDataTrack],
  }
}

export const detachTracks = (
  tracks: (LocalTrack | RemoteTrack | LocalTrackPublication)[],
  stopTracks: boolean
) => {
  tracks.forEach((track) => {
    if (!track) {
      return
    }

    if (stopTracks) {
      // Need the disable event to fire
      if ('disable' in track) {
        track.disable()
      }
      if ('stop' in track) {
        track.stop()
      }
    }

    if ('detach' in track) {
      track.detach().forEach((detachedElement) => {
        detachedElement.remove()
      })
    }
  })
}

export const getParticipantRoleFromType = (type: UserType) => {
  switch (type) {
    case UserType.PATIENT:
      return ParticipantRole.PATIENT
    case UserType.VIDEO_CALL_GUEST:
      return ParticipantRole.VIDEO_CALL_GUEST
    case UserType.USER:
      return ParticipantRole.USER
    case UserType.VISITOR:
      return ParticipantRole.VISITOR
    default:
      return ParticipantRole.INTERPRETER
  }
}

export const rtcSessionValid = (session?: RtcSession) =>
  [RtcSessionStatus.CREATED, RtcSessionStatus.CONNECTED].includes(
    session?.status as RtcSessionStatus
  )
